import type { FormValues } from "@/widgets/classic-form/form";
import { FieldComposer, fieldToConfig } from "@/features/form-fields";
import type { Field } from "@/entities/form-metadata";
import { NameAssertion } from "@/entities/form-metadata";
import type { GridSpan } from "@/shared/components/Grid";
import { Grid } from "@/shared/components/Grid";
import type { PrefixObjectAccessor } from "@/shared/forms";

interface NonSelfNameFieldProps {
    field: Field<NameAssertion>;
    relation: string;
    spanDesktop: GridSpan;
    onFocus?: () => void;
}

/**
 * Renders one field of a non-Self name group — a named party on the record other than the
 * search subject (e.g. an Executor/Informant, `relation: "Other1"`). There is no dataModel
 * slot for a second named person, so this reads/writes the open `fields` map under
 * `${relation}${assertion}`, the same convention `otherGroup.ts`/`OtherFieldsGroup` use for
 * generic non-Self fields, and uses the single isExact toggle rather than the compound
 * name-exactness control — a non-Self party has no phonetic/similar/initials/soundex flags in
 * this data shape. See `SelfNameField` for the primary search-subject name. F97/TT-3954.
 */
export const NonSelfNameField = ({ field, relation, spanDesktop, onFocus }: NonSelfNameFieldProps) => {
    const {
        assertion,
        features: { gridColumnSpan },
    } = field;
    const fieldPath = `fields.${relation}${assertion}`;

    return (
        <Grid.Field
            key={assertion}
            spanDesktop={(gridColumnSpan as GridSpan) ?? spanDesktop}
            spanTablet={(gridColumnSpan as GridSpan) ?? spanDesktop}
            spanMobile={12}
        >
            <FieldComposer
                fieldPath={`${fieldPath}.value` as PrefixObjectAccessor<FormValues, []>}
                exactnessFieldPath={`${fieldPath}.isExact` as PrefixObjectAccessor<FormValues, []>}
                fieldConfig={fieldToConfig(field)}
            >
                <FieldComposer.Label />
                <FieldComposer.FieldConnector>
                    {({ formField, fieldPath: connectedFieldPath, fieldConfig }, fieldHtmlId) => (
                        <FieldComposer.Inputs.String
                            id={fieldHtmlId}
                            data-testid={connectedFieldPath}
                            required={fieldConfig.required}
                            minLength={fieldConfig.minLength}
                            {...formField}
                            onFocus={onFocus}
                        />
                    )}
                </FieldComposer.FieldConnector>
                {assertion !== NameAssertion.SecondSurname && <FieldComposer.GenericExactness />}
            </FieldComposer>
        </Grid.Field>
    );
};
